import { useEffect, useRef, useState } from "react";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { User } from "types/app";
import {
  getQueryLocationID,
  getQueryParam,
  getQueryTrackID,
} from "utils/getQueryParam";
import { useAuthModals } from "./useAuthModals";
import { useGenericModals } from "./useGenericModals";

/**
 * Handles initial modals (welcome, verified/non-verified, password reset), shown once when the app opens
 */
export const useInitialModals = (user: User | null) => {
  const { openWelcomeModal, openInfoModal, openErrorModal } =
    useGenericModals();
  const { openResetPasswordModal, openSetNewPasswordModal } = useAuthModals();
  const [isWelcomeModalHidden] = useLocalStorage({
    key: "hideWelcomeModal",
    defaultValue: false,
    // The effect below runs right after the first render, so the stored value has to be read during it
    getInitialValueInEffect: false,
  });
  const { t } = useTranslation();
  // Read on the first render, before effects of other components can change the URL
  const [queryParams] = useState(() => ({
    passwordReset: getQueryParam("passwordreset"),
    forgotPassword: getQueryParam("forgotpassword"),
    verified: getQueryParam("verified"),
    locationId: getQueryLocationID(),
    trackId: getQueryTrackID(),
  }));
  const didRun = useRef(false);

  useEffect(() => {
    // Only on the first run: the user signing in later or the translations loading mustn't open another modal
    if (didRun.current) return;
    didRun.current = true;

    const { passwordReset, forgotPassword, verified, locationId, trackId } =
      queryParams;

    if (passwordReset) {
      openSetNewPasswordModal(passwordReset);
      return;
    }

    if (forgotPassword) {
      openResetPasswordModal();
      return;
    }

    // The app was opened via a shared location link
    if (locationId) return;

    if (verified) {
      if (Number(verified) === 1) {
        openInfoModal(t("verified_modal.title"), t("verified_modal.body"));
      } else {
        openErrorModal({
          title: t("not_verified_modal.title"),
          text: t("not_verified_modal.body"),
        });
      }
      return;
    }

    // Not shown to signed in users and when the app was opened via a shared track link
    if (!isWelcomeModalHidden && !user && !trackId) {
      void openWelcomeModal();
    }
  }, [
    isWelcomeModalHidden,
    queryParams,
    openErrorModal,
    openInfoModal,
    openResetPasswordModal,
    openSetNewPasswordModal,
    openWelcomeModal,
    t,
    user,
  ]);
};
